import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-scrollable',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './scrollable.component.html',
  styleUrls: ['./scrollable.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ScrollableComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scrollContent') scrollContentRef!: ElementRef<HTMLElement>;
  @ViewChild('scrollTrack')   scrollTrackRef!:   ElementRef<HTMLElement>;

  enabled = input<boolean>(true);

  private readonly _cdr = inject(ChangeDetectorRef);

  thumbHeight   = 0;
  thumbTop      = 0;
  showScrollbar = false;

  private _isDragging         = false;
  private _dragStartY         = 0;
  private _dragStartScrollTop = 0;
  private _resizeObserver?: ResizeObserver;
  private readonly _onMouseMove = this._handleMouseMove.bind(this);
  private readonly _onMouseUp   = this._handleMouseUp.bind(this);

  private _viewReady = false;
  private _active    = false;

  constructor() {
    effect(() => {
      const enabled = this.enabled();
      if (!this._viewReady) return;
      if (enabled) this._enable();
      else this._disable();
    });
  }

  ngAfterViewInit(): void {
    this._viewReady = true;
    if (this.enabled()) this._enable();
  }

  ngOnDestroy(): void {
    this._teardown();
  }

  private _enable(): void {
    if (this._active) return;
    this._active = true;
    requestAnimationFrame(() => requestAnimationFrame(() => this._computeScrollbar()));
    this._resizeObserver = new ResizeObserver(() => this._computeScrollbar());
    this._resizeObserver.observe(this.scrollContentRef.nativeElement);
  }

  /** Désactive la scrollbar custom (runtime) : teardown + masquage. */
  private _disable(): void {
    if (!this._active) return;
    this._teardown();
    if (this.showScrollbar) {
      this.showScrollbar = false;
      this._cdr.detectChanges();
    }
  }

  private _teardown(): void {
    this._active = false;
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
    this._isDragging = false;
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
  }

  private _computeScrollbar(): void {
    const content = this.scrollContentRef?.nativeElement;
    if (!content) return;
    const needsScroll = content.scrollHeight > content.clientHeight;
    if (needsScroll !== this.showScrollbar) {
      this.showScrollbar = needsScroll;
      this._cdr.detectChanges();
    }
    if (this.showScrollbar) requestAnimationFrame(() => this._computeThumb());
  }

  private _computeThumb(): void {
    const content = this.scrollContentRef?.nativeElement;
    const track   = this.scrollTrackRef?.nativeElement;
    if (!content || !track) return;
    this.thumbHeight = Math.max(32, (content.clientHeight / content.scrollHeight) * track.clientHeight);
    this._updateThumbPosition();
    this._cdr.detectChanges();
  }

  private _updateThumbPosition(): void {
    const content = this.scrollContentRef?.nativeElement;
    const track   = this.scrollTrackRef?.nativeElement;
    if (!content || !track) return;
    const maxScroll   = content.scrollHeight - content.clientHeight;
    const scrollRatio = maxScroll > 0 ? content.scrollTop / maxScroll : 0;
    this.thumbTop     = scrollRatio * (track.clientHeight - this.thumbHeight);
  }

  onScroll(): void {
    this._updateThumbPosition();
  }

  onTrackClick(event: MouseEvent): void {
    const track   = this.scrollTrackRef.nativeElement;
    const content = this.scrollContentRef.nativeElement;
    const clickY  = event.clientY - track.getBoundingClientRect().top;
    content.scrollTop = (clickY / track.clientHeight) * (content.scrollHeight - content.clientHeight);
    this._updateThumbPosition();
  }

  onThumbMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this._isDragging         = true;
    this._dragStartY         = event.clientY;
    this._dragStartScrollTop = this.scrollContentRef.nativeElement.scrollTop;
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup',   this._onMouseUp);
  }

  private _handleMouseMove(event: MouseEvent): void {
    if (!this._isDragging) return;
    const content = this.scrollContentRef.nativeElement;
    const track   = this.scrollTrackRef.nativeElement;
    const deltaY  = event.clientY - this._dragStartY;
    const ratio   = (content.scrollHeight - content.clientHeight) / (track.clientHeight - this.thumbHeight);
    content.scrollTop = this._dragStartScrollTop + deltaY * ratio;
    this._updateThumbPosition();
  }

  private _handleMouseUp(): void {
    this._isDragging = false;
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
  }
}
